import { useEffect, useState } from 'react'
import { api, type Retirement, type Rule } from '../api'
import { RowMenu } from './RowMenu'
import { says } from '../limits'

/**
 * Limits proposed for retirement, waiting for an approver — decision 72.
 *
 * Approving ends the limit from the day proposed: its period closes and it stays the
 * right answer for every day it applied. Declining leaves it in force. Nobody decides
 * a retirement they proposed.
 */
export function Retirements({ canApprove, me }: { canApprove: boolean; me?: string }) {
  const [items, setItems] = useState<Retirement[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function load() {
    try {
      const found = await api.retirements()
      setItems(Array.isArray(found) ? found : [])
    } catch {
      // The page's own list says when the store cannot be reached; a second alert
      // for the same outage says nothing more.
      setItems([])
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function decide(item: Retirement, decision: 'approve' | 'decline') {
    const note =
      decision === 'decline' ? window.prompt(`Why is ${item.rule_key} staying in force?`) ?? undefined : undefined
    if (decision === 'decline' && note === undefined) return
    setBusy(item.id)
    setError(null)
    try {
      await api.decideRetirement(item.id, decision, note)
      setNotice(
        decision === 'approve'
          ? `${item.rule_key} no longer applies from ${item.ends_on}.`
          : `${item.rule_key} stays in force.`,
      )
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  if (items.length === 0 && !notice && !error) return null

  return (
    <section className="retirements" data-testid="retirements">
      <h3>Limits proposed for retirement ({items.length})</h3>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="ok">{notice}</p>}
      {items.length > 0 && (
        <table className="grid limits has-menus">
          <thead>
            <tr>
              <th>Limit</th>
              <th>Says</th>
              <th>Applies to</th>
              <th>No longer applies from</th>
              <th>Why</th>
              {canApprove && <th />}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const own = !!me && item.proposed_by_user_id === me
              const said = says(item as unknown as Rule)
              return (
                <tr key={item.id} data-testid="retirement">
                  <td>
                    <code>{item.rule_key}</code>
                    <span className="channel">proposed by {own ? 'you' : item.proposed_by}</span>
                  </td>
                  <td>
                    <span className={said.numeric ? 'limit' : 'no-number'}>{said.text}</span>
                  </td>
                  <td>
                    {item.portfolio_code ?? (
                      <>
                        group <code>{item.scope_code}</code>, {item.funds_affected} fund(s)
                      </>
                    )}
                  </td>
                  <td>{item.ends_on}</td>
                  <td>{item.reason}</td>
                  {canApprove && (
                    <td className="actions">
                      <RowMenu
                        label={`Decide the retirement of ${item.rule_key}`}
                        items={[
                          {
                            label: 'Approve',
                            disabled: busy === item.id || own,
                            why: own ? 'You proposed this, so another approver decides it.' : undefined,
                            onSelect: () => void decide(item, 'approve'),
                          },
                          {
                            label: 'Decline',
                            danger: true,
                            disabled: busy === item.id,
                            onSelect: () => void decide(item, 'decline'),
                          },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}
